import React from 'react';
import { useCookies } from 'react-cookie';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Grid, Nav } from '..';
import styles from './footer.module.scss';

const SUPPORT_ID_COOKIE = 'fullstory_support_id';

const links = [
  {
    href: {
      en: 'https://www.sonnet.ca/contact-us',
      fr: 'https://www.sonnet.ca/pour-nous-joindre',
    },
    i18nKeyId: 'footer.links.contact',
    col3: 1,
    col2: 1,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/claims',
      fr: 'https://www.sonnet.ca/reclamations',
    },
    i18nKeyId: 'footer.links.claims',
    col3: 4,
    col2: 3,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/terms-of-use',
      fr: 'https://www.sonnet.ca/modalites-utilisation',
    },
    i18nKeyId: 'footer.links.terms',
    col3: 7,
    col2: 5,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/privacy',
      fr: 'https://www.sonnet.ca/confidentialite',
    },
    i18nKeyId: 'footer.links.privacy',
    col3: 2,
    col2: 7,
  },
  {
    href: {
      en: "javascript:window.semaphore.push(['showPreferences']);",
      fr: "javascript:window.semaphore.push(['showPreferences']);",
    },
    i18nKeyId: 'footer.links.privacyPreference',
    col3: 2,
    col2: 7,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/recurring-payment-agreement',
      fr: 'https://www.sonnet.ca/entente-de-paiement-periodique',
    },
    i18nKeyId: 'footer.links.recurringPayments',
    col3: 5,
    col2: 9,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/accessibility',
      fr: 'https://www.sonnet.ca/accessibilite',
    },
    i18nKeyId: 'footer.links.accessibility',
    col3: 8,
    col2: 2,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/compliments-and-complaints',
      fr: 'https://www.sonnet.ca/compliments-et-plaintes',
    },
    i18nKeyId: 'footer.links.compliments',
    col3: 3,
    col2: 4,
  },
  {
    href: {
      en: 'https://www.sonnet.ca/faq',
      fr: 'https://www.sonnet.ca/faq',
    },
    i18nKeyId: 'footer.links.faqs',
    col3: 6,
    col2: 6,
  },
  {
    href: null, //lgSwitch
    col3: 9,
    col2: 8,
  },
];

const contactUrls = links[0].href;

const Footer = ({ setLanguage, supportID, ...props }) => {
  const [cookies] = useCookies();
  const displayedSupportID = supportID ? supportID : cookies[SUPPORT_ID_COOKIE];
  const { locale } = useIntl();

  const LinkBtn = (link, i) => (
    <div key={link.href + i} className={styles.footerLink}>
      {link.href ? (
        <Button
          href={link.href[locale]}
          className={styles.navLink}
          link
          newTab
          underline
          primary
        >
          <FormattedMessage id={link.i18nKeyId} />
        </Button>
      ) : (
        <Nav.TranslateLink
          className={styles.navLink}
          link
          underline
          primary
          onClick={setLanguage}
        />
      )}
    </div>
  );

  return (
    // eslint-disable-next-line react/prop-types
    <div className={cn(styles.footer, props.className)}>
      <Grid className={styles.content} narrow>
        <Grid.Row noGutters>
          <Grid.Column>
            <FormattedMessage
              id="footer.supportID"
              defaultMessage="Support ID"
            />{' '}
            {displayedSupportID}
          </Grid.Column>
        </Grid.Row>
        <div className={cn(styles.links)}>
          <div className={styles.defaultLinks}>{links.map(LinkBtn)}</div>
        </div>

        <p>
          <FormattedMessage
            id="footer.messages.copyright"
            description="The copyright message"
            defaultMessage="©{year} Sonnet Insurance. Sonnet Insurance® All trademarks used herein are owned by the respective entities. All rights reserved. Sonnet may be contacted at {contactLink}"
            values={{
              year: new Date().getFullYear(),
              contactLink:
                locale === 'en' ? (
                  <Button
                    href={contactUrls.en}
                    className={styles.link}
                    link
                    newTab
                  >
                    {contactUrls.en}
                  </Button>
                ) : (
                  <Button
                    href={contactUrls.fr}
                    className={styles.link}
                    link
                    newTab
                  >
                    {contactUrls.fr}
                  </Button>
                ),
            }}
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.autorite"
            description="The autorite message"
            defaultMessage="Policies underwritten by Sonnet Insurance Company except for Sonnet Pet Insurance. Sonnet Insurance Company is registered with Autorité des marches financiers as a property and casualty insurer (Reg #: 2000434353) and damage insurance agency (Reg #: 50519). Contact Sonnet in either capacity via chat, electronic note, or by logging into the customer portal at {contactLink}."
            values={{
              contactLink:
                locale === 'en' ? (
                  <Button
                    href={contactUrls.en}
                    className={styles.link}
                    link
                    newTab
                  >
                    {contactUrls.en}
                  </Button>
                ) : (
                  <Button
                    href={contactUrls.fr}
                    className={styles.link}
                    link
                    newTab
                  >
                    {contactUrls.fr}
                  </Button>
                ),
            }}
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.customerExperience"
            description="The customer experience message"
            defaultMessage="The 5 minutes to get insured is based on the average Sonnet customer experience. Your customer experience will be based on your information and the products that we have to service your insurance needs. We reserve the right to verify this information. Quotes provided are valid for 45 days, and are intended to be an estimate only. Actual premiums, rates and terms of coverage will only be determined after you submit an application for insurance which is accepted by Sonnet Insurance Company. Always refer to your insurance policy for information about your coverage, including limits, deductibles, and exclusions. In case of discrepancy, the insurance contract prevails over general information available on this site. Any policy related changes made on this site will not be effective until you receive confirmation."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.bundling"
            description="The bundling message"
            defaultMessage="Bundling discount reflects the best possible savings a customer could receive by bundling their home and auto coverage. Depending on your quote details, you may not be eligible for a discount."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.partners"
            description="The partners message"
            defaultMessage="Homewise, Carfax, Wealthsimple, Moka, KOHO, Borrowell, Intuit Canada, Willful, Turnip Home Inc, Car Loans Canada, Deeded, Nissan Canada Inc., and FLO have commercial marketing partnerships with Sonnet. Petline Insurance Company is an affiliate of Sonnet who underwrites and distributes Sonnet Pet Insurance. Sonnet offers no assurances that any of these partners’ products or services are suitable for any particular individual."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.parachute"
            description="The parachute digital solutions message"
            defaultMessage="Parachute Digital Solutions Inc. has a referral arrangement with Sonnet."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.nissan"
            description="The nissan message"
            defaultMessage="Nissan names, logos and trademarks are owned by or licensed to Nissan Motor Co, Ltd. and/or its North American subsidiaries. Nissan Canada Inc., is neither an insurance company nor an insurance broker and is not providing insurance to the public."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.infiniti"
            description="The infiniti message"
            defaultMessage="INFINITI names, logos and trademarks are owned by or licensed to Nissan Motor Co, Ltd. and/or its North American subsidiaries. INFINITI a division of Nissan Canada Inc. Nissan Canada Inc., is neither an insurance company nor an insurance broker and is not providing insurance to the public."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.carLoansCanada"
            description="The car loans canada message"
            defaultMessage="Car Loans Canada Trademark shall include any word, name, symbol, colour, designation or device or any combination thereof that functions as a source identifier, including any trademark, trade dress, service mark, trade name, logo, design mark or domain name, whether or not registered."
          />
        </p>
        <p>
          <FormattedMessage
            id="footer.messages.offersDisclaimer"
            description="The offers disclaimer message"
            defaultMessage="All offers are only available while displayed on this site and are subject to termination without notice."
          />
        </p>
      </Grid>
    </div>
  );
};

Footer.propTypes = {
  /** The callback function called when the language switch button is clicked */
  setLanguage: PropTypes.func,
  /** The support ID string */
  supportID: PropTypes.string,
};

Footer.defaultProps = {
  setLanguage: undefined,
  supportID: '',
};

export default Footer;
